"use client";
import { useId } from "react";
import { useMediaQuery } from "@/hooks/use-media-query";
import { Button } from "@/components/ui/button";
import { InfoTooltip } from "@/components/ui/info-tooltip";
import { Play, Pause, SkipBack, SkipForward } from "lucide-react";
import { areTeamsSwitched } from "@/lib/replay-utils";
import type { ReplayData } from "@/lib/demo-parser";

interface ReplayControlsProps {
  replayData: ReplayData;
  displayFrame: number;
  isPlaying: boolean;
  playbackSpeed: number;
  totalFrames: number;
  onPlayPause: () => void;
  onSkipForward: () => void;
  onSkipBackward: () => void;
  onSeek: (frame: number) => void;
  onSpeedChange: (speed: number) => void;
  onRoundClick: (roundNumber: number) => void;
  roundOnly?: number | null;
}

export function ReplayControls({
  replayData,
  displayFrame,
  isPlaying,
  playbackSpeed,
  totalFrames,
  onPlayPause,
  onSkipForward,
  onSkipBackward,
  onSeek,
  onSpeedChange,
  onRoundClick,
  roundOnly = null,
}: ReplayControlsProps) {
  const controlId = useId();
  const mobile = useMediaQuery("(max-width: 1023px)");
  const currentRoundNumber = replayData.frames[displayFrame]?.roundNumber || 1;

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSeek(parseInt(e.target.value));
  };

  if (mobile) return (
    <section aria-label="Playback controls" className="replay-controls-mobile min-w-0 rounded-xl border border-border bg-surface-1 p-2">
      <div className="grid grid-cols-2 gap-2">
        <label htmlFor={`${controlId}-round`} className="sr-only">Round</label>
        <select id={`${controlId}-round`} value={currentRoundNumber} onChange={(event) => onRoundClick(Number(event.target.value))} className="h-11 min-w-0 rounded-lg border border-border bg-surface-2 px-2 text-sm text-foreground">
          {replayData.roundStartTicks.map((_, index) => index + 1).filter((round) => !roundOnly || round === roundOnly).map((round) => <option key={round} value={round}>Round {round}</option>)}
        </select>
        <label htmlFor={`${controlId}-speed`} className="sr-only">Playback speed</label>
        <select id={`${controlId}-speed`} value={playbackSpeed} onChange={(event) => onSpeedChange(Number(event.target.value))} className="h-11 min-w-0 rounded-lg border border-border bg-surface-2 px-2 text-sm text-foreground">
          {[0.5, 1, 2, 4].map((speed) => <option key={speed} value={speed}>{speed}× speed</option>)}
        </select>
      </div>
      <div className="flex h-11 items-center gap-2">
        <input aria-label="Replay timeline" type="range" min={0} max={Math.max(0, totalFrames - 1)} value={displayFrame} onChange={handleSeek} className="h-11 min-w-0 flex-1 accent-primary" />
        <span className="shrink-0 text-xs tabular-nums text-fg-secondary">{displayFrame + 1} / {totalFrames}</span>
      </div>
      <div className="flex justify-center gap-3">
        <Button onClick={onSkipBackward} size="icon" variant="outline" aria-label="Skip backward"><SkipBack className="size-4" /></Button>
        <Button onClick={onPlayPause} size="icon" aria-label={isPlaying ? "Pause replay" : "Play replay"}>{isPlaying ? <Pause className="size-5" /> : <Play className="size-5" />}</Button>
        <Button onClick={onSkipForward} size="icon" variant="outline" aria-label="Skip forward"><SkipForward className="size-4" /></Button>
      </div>
    </section>
  );

  return (
    <div className="sticky bottom-0 z-20 rounded-xl border border-white/10 bg-surface-1/96 p-3 shadow-2xl backdrop-blur-xl sm:p-4">
      <div className="space-y-3">
        {/* Rounds bar */}
        <div className="flex flex-wrap items-start gap-3 sm:items-center sm:gap-4">
          <div className="flex items-center gap-2 whitespace-nowrap">
            <span className="text-xs text-neutral-400">Rounds</span>
            <InfoTooltip
              content="Each square jumps to a round. Blue and yellow reflect the side that won that round on the server timeline, so colors flip after halftime and each overtime side swap."
              panelClassName="left-0 -translate-x-0"
            />
          </div>
          <div className="flex gap-1 flex-wrap flex-1">
            {replayData.roundStartTicks.map((_, index) => {
              const roundNumber = index + 1;
              if (roundOnly && roundNumber !== roundOnly) return null;
              const winner = replayData.roundWinners[index];
              const isCurrentRound = currentRoundNumber === roundNumber;
              const teamsHaveSwitched = areTeamsSwitched(roundNumber);

              let bgColor = "bg-white/20";
              if (winner === 1) {
                bgColor = teamsHaveSwitched ? "bg-[#f0c941]" : "bg-blue-500";
              } else if (winner === 2) {
                bgColor = teamsHaveSwitched ? "bg-blue-500" : "bg-[#f0c941]";
              }

              return (
                <button
                  key={roundNumber}
                  onClick={() => onRoundClick(roundNumber)}
                  className={`
                    w-7 h-7 rounded text-xs font-medium transition-all
                    ${bgColor}
                    ${isCurrentRound
                      ? "ring-2 ring-white ring-offset-1 ring-offset-[#0B0D12] scale-110"
                      : "hover:scale-105 hover:brightness-110"
                    }
                    ${winner ? "text-black" : "text-neutral-300"}
                  `}
                  title={`Round ${roundNumber}${winner ? ` - Team ${winner} won` : ""}`}
                  aria-label={`Go to round ${roundNumber}${winner ? `, Team ${winner} won` : ""}`}
                >
                  {roundNumber}
                </button>
              );
            })}
          </div>
          <div className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
            <div className="flex items-center gap-1">
              <div className="w-2.5 h-2.5 rounded-sm bg-blue-500" />
              <span>CT</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2.5 h-2.5 rounded-sm bg-[#f0c941]" />
              <span>T</span>
            </div>
          </div>
        </div>

        {/* Timeline slider */}
        <div className="flex items-center gap-2 sm:gap-4">
          <div className="flex items-center gap-2">
            <span className="text-xs text-neutral-500">Timeline</span>
            <InfoTooltip
              content="Drag the scrubber to move frame-by-frame through the parsed replay. The counter on the right is the current frame index, not the in-game tick."
              panelClassName="left-0 -translate-x-0"
            />
          </div>
          <input
            aria-label="Replay timeline"
            type="range"
            min={0}
            max={totalFrames - 1}
            value={displayFrame}
            onChange={handleSeek}
            className="flex-1 h-2 bg-white/10 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-500"
          />
          <span className="w-20 text-right text-xs text-neutral-400 sm:w-32">
            {displayFrame} / {totalFrames}
          </span>
        </div>

        {/* Playback buttons */}
        <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
          <Button
            onClick={onSkipBackward}
            variant="outline"
            size="icon"
            className="bg-white/10 border-white/20 w-9 h-9"
            title="Skip backward"
            aria-label="Skip backward"
          >
            <SkipBack className="w-4 h-4" />
          </Button>

          <Button
            onClick={onPlayPause}
            size="icon"
            className="bg-blue-500 hover:bg-blue-400 w-11 h-11"
            title={isPlaying ? "Pause replay" : "Play replay"}
            aria-label={isPlaying ? "Pause replay" : "Play replay"}
          >
            {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
          </Button>

          <Button
            onClick={onSkipForward}
            variant="outline"
            size="icon"
            className="bg-white/10 border-white/20 w-9 h-9"
            title="Skip forward"
            aria-label="Skip forward"
          >
            <SkipForward className="w-4 h-4" />
          </Button>

          {/* Speed controls */}
          <div className="flex flex-wrap gap-1.5 sm:ml-4">
            <div className="flex items-center gap-2 pr-1">
              <span className="text-xs text-neutral-500">Speed</span>
              <InfoTooltip
                content="Playback speed only affects the viewer clock. It does not resample demo data or change the parsed event timing."
                panelClassName="left-0 -translate-x-0"
              />
            </div>
            {[0.5, 1, 2, 4].map((speed) => (
              <Button
                key={speed}
                onClick={() => onSpeedChange(speed)}
                size="sm"
                title={`Set playback speed to ${speed}x`}
                className={`h-8 px-2.5 text-xs transition-colors ${
                  playbackSpeed === speed
                    ? "bg-blue-700 text-white border-blue-700 hover:bg-blue-600 border"
                    : "bg-white/10 text-white border-white/20 hover:bg-white/20 border"
                }`}
              >
                {speed}x
              </Button>
            ))}
          </div>

          {/* Legend */}
          <div className="hidden items-center gap-4 text-xs text-neutral-400 xl:ml-4 xl:flex">
            <div className="flex items-center gap-2">
              <span>Legend</span>
              <InfoTooltip
                content="Player dots use the current live side colors, not fixed team identities. That means the same roster can appear blue in one half and yellow in another."
                panelClassName="right-0 left-auto -translate-x-0"
              />
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-blue-500" />
              <span>CT</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-[#f0c941]" />
              <span>T</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-yellow-500" />
              <span>Fire</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 bg-red-500" />
              <span>Death</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
